import React, { useId, useRef, useState, useContext } from "react";
import styles from "./modal.module.css";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogProps,
	DialogTitle,
	SvgIcon,
	SxProps,
	Box,
	DialogTitleProps,
	Typography,
	TypographyProps,
} from "@mui/material";
import { ArrowBackIos } from "@mui/icons-material";
import Close from "../images/close-x.svg?react";
import { CoreButton, CoreButtonColors, CoreButtonCustomColor, CoreIconButton } from "../button/Button";
import { getScrollbarThemeOverrides } from "../scrollbar";
import CoreFocusTrap from "../focus/FocusTrap";
import { useMobileLayout } from "../../hooks";
import { ModalContext } from "./context";
import i18nPackage from "../../i18n/i18n";

type OnClickType = React.MouseEventHandler<HTMLButtonElement>;

type DialogCloseButtonProps = {
	disabled?: boolean;
	onClose?: OnClickType;
};

type DialogBackButtonProps = {
	onBack: () => void;
};

export type CoreModalProps = {
	/** Determine the contents in the modal header. If not defined, the header will not render. */
	title?: React.ReactNode;
	/** If provided, the dialog header will include a back button that calls this function on click. */
	onBack?: () => void;
	/** If `true`, the modal will use an older style. @default false */
	old?: boolean;
	/** If `true`, the top close button will be rendered. @default true */
	showTopCloseButton?: boolean;
	/** If `true`, the top close button is disabled. @default false */
	disableTopCloseButton?: boolean;

	/** If `true`, the rightmost action is an additional "Close" button. @default false */
	showBottomCloseButton?: boolean;
	/** If `true`, the bottom actions footer will not be rendered. @default false */
	noActions?: boolean;
	/** Determine the buttons in the actions footer  */
	buttons?:
		| (Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color" | "hidden"> & {
				/** Content of the button */
				text?: React.ReactNode;
				/** The color of the component. @default "primary" */
				color?: CoreButtonColors;
				/** A custom color that replaces `color` */
				customColor?: CoreButtonCustomColor;
				/** Element placed before the children. */
				startIcon?: React.ReactNode;
				/** Element placed after the children. */
				endIcon?: React.ReactNode;
				/** If `true`, the button is disabled. @default false */
				disabled?: boolean;
				/** If `true`, the button is not rendered. @default false */
				hide?: boolean;
				/** The system prop that allows defining system overrides as well as additional CSS styles. */
				sx?: SxProps;
				/** Callback fired when the component is clicked. */
				onClick?: OnClickType;
		  })[]
		| React.ReactNode;

	/** Determine the width of the modal. Ignored if `widthFixed`is `true` */
	width?: string;
	/** Determine the minimum width of the modal. If `widthFixed` is `true`, it is also the maximum width. */
	minWidth?: string;
	/** Determine the minimum height of the modal. If `heightFixed` is `true`, it is also the maximum height. */
	minHeight?: string;
	/** Determine the maximum width of the modal. Ignored if `widthFixed` is `true`. */
	maxWidth?: string;
	/** Determine the maximum height of the modal. Ignored if `heightFixed` is `true`. */
	maxHeight?: string;
	/** If `true`, the modal width will not change with its content's width.
	 * If `minWidth` is provided, the width is fixed at `minWidth`, otherwise it is fixed at the full width.
	 * @default false
	 * */
	widthFixed?: boolean;
	/** If `true`, the modal width will not change with its content's height.
	 * If `minHeight` is provided, the width is fixed at `minHeight`, otherwise it is fixed at the full height.
	 * @default false
	 * */
	heightFixed?: boolean;
	/** The padding of the content.
	 * @default "24px"
	 */
	padding?: string | number;
	/** The margin of the content. */
	margin?: string | number;
	/** The margin of the modal
	 */
	modalMargin?: string | number;
	/** The width and height of the content scrollbar.
	 * @default "12px"
	 */
	scrollbarWidth?: string | number;
	/** If `true`, the modal content will expand its height to match the modal. @default false */
	contentMatchModalHeight?: boolean;

	children?: React.ReactNode;
	/** Callback fired when the component requests to be closed. */
	onClose?: OnClickType;
	/** Callback fired when the top close button is clicked, will default to `onClose` if not defined. */
	onTopClose?: OnClickType;
	zIndex?: number;
	/** Vertical position of the dialog. */
	modalVerticalPosition?: "top" | "bottom";
	/** Keep the top border radius of the modal even when modalVerticalPosition is "top" */
	keepTopBorderRadius?: boolean;

	equalButtons?: boolean;
	/** Vertical padding for the <DialogHeader /> */
	verticalPadding?: string | number;
	/** Vertical padding for the <DialogFooter /> */
	footerVerticalPadding?: string | number;
	/** Horizontal padding for the <DialogFooter /> */
	footerHorizontalPadding?: string | number;
	/** Optional Id for header. Used for aria-labelledby */
	headerId?: string;
	/** Modal takes up the whole page. Used for mobile layouts. (Sometimes, mobile layouts show normal modals too) */
	fullScreen?: boolean;
	/**For Accessibility. If true, on modal open, the screen reader will read the modal's title immediately. It makes the modal have role = alertdialog. */
	alertModal?: boolean;
	/**For accessibility. If this is an alert modal, pass id of description text. `alertDescriptionId` will be used for aria-describedby */
	alertDescriptionId?: string;
	/** If `true`, ensures the modal captures focus on open */
	forceFocus?: boolean;
	/** Background colour of the DialogContent */
	dialogContentBackground?: string;
	/** Sets the dialog's overflow-x to "hidden" */
	hideHorizontalOverflow?: boolean;
	/** Sets the dialog's overflow-y to "hidden" */
	hideVerticalOverflow?: boolean;
	/** Border of the Dialog Paper */
	border?: string;
	/** Box shadow for the Dialog Paper */
	boxShadow?: string;
	/** Background image of the DialogPaper */
	backgroundImage?: string;
} & Pick<DialogProps, "open" | "disableEscapeKeyDown" | "scroll" | "disablePortal" | "onClose">;

const DialogHeader = ({
	children,
	verticalPadding,
	ariaId, // For aria labelledby
}: {
	children?: React.ReactNode;
	verticalPadding?: string | number;
	ariaId?: string;
}) => {
	return (
		<DialogTitle
			className={styles["dialog-header"]}
			component="div"
			style={{
				paddingTop: verticalPadding,
				paddingBottom: verticalPadding,
			}}
			id={ariaId}
		>
			<Typography variant="h2" className={styles["dialog-title"]}>
				{children}
			</Typography>
		</DialogTitle>
	);
};

function DialogHeaderWithBackButton(
	props: {
		onBack: () => void;
		titleId?: string;
	} & Omit<DialogTitleProps<"div", { component: "div" }>, "component">,
) {
	const { children, titleId, sx, ...remainingProps } = props;
	return (
		<DialogTitle
			className={styles["dialog-header"]}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...remainingProps}
			component="div"
			sx={{ ...sx, padding: 0 }}
		>
			<Box flex={1} display="flex" alignItems="center">
				<DialogBackButton onBack={props.onBack} />
				<Typography variant="h2" className={styles["dialog-title"]} sx={{ flex: 1, fontSize: "inherit" }} id={titleId}>
					{children}
				</Typography>
				{
					<Box visibility="hidden">
						<DialogBackButton onBack={props.onBack} />
					</Box>
				}
			</Box>
		</DialogTitle>
	);
}

const DialogFooter = ({
	children,
	footerVerticalPadding,
	footerHorizontalPadding,
}: {
	children?: React.ReactNode;
	footerVerticalPadding?: string | number;
	footerHorizontalPadding?: string | number;
}) => {
	return (
		<DialogActions
			className={styles["dialog-footer"]}
			sx={{
				padding: `${footerVerticalPadding ?? "15px"} ${footerHorizontalPadding ?? "20px"}`,
			}}
		>
			{children}
		</DialogActions>
	);
};

const DialogCloseButton = ({ onClose, disabled }: DialogCloseButtonProps) => {
	return (
		<CoreIconButton
			className={`${styles["close-button"]}`}
			onClick={onClose}
			size="medium"
			sx={{
				margin: "8px",
				color: "#6B6B6B",
			}}
			disabled={disabled}
			aria-label={i18nPackage.t("coreComponents.modal.closeButtonAriaLabel")}
			variant="plain"
		>
			<Close />
		</CoreIconButton>
	);
};

const DialogBackButton = ({ onBack }: DialogBackButtonProps) => {
	return (
		<Button
			variant="text"
			className={styles["back-button"]}
			onClick={onBack}
			disableFocusRipple
			startIcon={
				<SvgIcon>
					<ArrowBackIos />
				</SvgIcon>
			}
		>
			{i18nPackage.t("coreComponents.modal.back")}
		</Button>
	);
};

export const CoreModal = ({
	open,
	onClose,
	onBack,
	title,
	old,
	showTopCloseButton = true,
	disableTopCloseButton,
	showBottomCloseButton,
	noActions,
	buttons,
	width,
	minWidth,
	minHeight,
	maxWidth,
	maxHeight,
	widthFixed,
	heightFixed,
	padding,
	margin,
	modalMargin,
	scrollbarWidth,
	children,
	disableEscapeKeyDown,
	scroll,
	disablePortal,
	contentMatchModalHeight,
	onTopClose,
	zIndex,
	modalVerticalPosition,
	keepTopBorderRadius,
	equalButtons,
	verticalPadding,
	footerVerticalPadding,
	headerId,
	fullScreen,
	alertModal = false,
	alertDescriptionId,
	forceFocus,
	dialogContentBackground,
	hideHorizontalOverflow,
	hideVerticalOverflow,
	border,
	boxShadow,
	backgroundImage,
}: CoreModalProps) => {
	const dataTheme = old ? "old" : "";

	let verticalFlexAlign: string | undefined;
	if (modalVerticalPosition === "top") {
		verticalFlexAlign = "start";
	} else if (modalVerticalPosition === "bottom") {
		verticalFlexAlign = "end";
	} else {
		verticalFlexAlign = "center";
	}

	let modalMarginValue = modalMargin;
	if (modalVerticalPosition && !modalMargin) {
		modalMarginValue = 0;
	} else if (modalMargin == null) {
		modalMarginValue = "32px";
	}

	const isMobileLayout = useMobileLayout();
	const { modalContainer } = useContext(ModalContext);

	// Known issue where focus doesnt stay trapped in dialog while in shadow dom
	// https://github.com/mui/material-ui/issues/34980
	const [inShadowDom, setInShadowDom] = useState(false);
	const firstFocusableRef = useRef<HTMLElement | null>(null);
	const lastFocusableRef = useRef<HTMLElement | null>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);

	const alertModalId = useId();
	// Title gets an id if user passes an id or if modal is an alert modal
	const titleId = headerId ?? (alertModal ? alertModalId : undefined);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xl"
			data-theme={dataTheme}
			className="CoreModal-dialog"
			hideBackdrop={fullScreen}
			container={isMobileLayout ? modalContainer.current : undefined}
			sx={{
				"&.CoreModal-dialog .MuiDialog-container": {
					"& .MuiPaper-root.MuiDialog-paper": {
						margin: modalMarginValue,
						width: widthFixed ? "100%" : width,
						height: heightFixed ? "100%" : undefined,
						minWidth: minWidth ?? (fullScreen ? "100%" : undefined),
						maxWidth: widthFixed ? minWidth : maxWidth,
						minHeight: minHeight ?? (fullScreen ? "100%" : undefined),
						maxHeight: heightFixed ? minHeight : maxHeight,
						border: border ?? undefined,
						borderRadius: fullScreen ? "0px" : "15px",
						borderTopLeftRadius: modalVerticalPosition === "top" && !keepTopBorderRadius ? 0 : undefined,
						borderTopRightRadius: modalVerticalPosition === "top" && !keepTopBorderRadius ? 0 : undefined,
						borderBottomLeftRadius: modalVerticalPosition === "bottom" ? 0 : undefined,
						borderBottomRightRadius: modalVerticalPosition === "bottom" ? 0 : undefined,
						overflowX: hideHorizontalOverflow ? "hidden" : undefined,
						overflowY: hideVerticalOverflow ? "hidden" : undefined,
						boxShadow: boxShadow ?? undefined,
						backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
					},
					alignItems: verticalFlexAlign,
				},
				"&.MuiDialog-root": {
					position: fullScreen ? "unset" : undefined,
					width: fullScreen ? "100%" : undefined,
					height: fullScreen ? "100%" : undefined,
				},
				zIndex,
			}}
			disableEscapeKeyDown={disableEscapeKeyDown}
			scroll={scroll}
			disablePortal={disablePortal}
			PaperProps={{
				ref: dialogRef,
				"aria-modal": "true",
				role: alertModal ? "alertdialog" : "dialog",
				sx: {
					"&:focus-visible": {
						outline: "none",
					},
				},
				style: {
					pointerEvents: isMobileLayout ? "auto" : undefined,
					margin: fullScreen ? 0 : undefined,
					top: fullScreen ? 0 : undefined,
					left: fullScreen ? 0 : undefined,
					width: fullScreen ? "100%" : undefined,
					height: fullScreen ? "calc(100% - 50px)" : undefined,
				},
				tabIndex: alertModal || forceFocus ? -1 : undefined,
				...(alertModal && {
					"aria-labelledby": titleId,
					"aria-describedby": alertDescriptionId,
				}),
			}}
			TransitionProps={{
				onEntered: () => {
					if (!dialogRef.current) return;
					const root = dialogRef.current.getRootNode();
					const isInShadowDom = root instanceof ShadowRoot;
					setInShadowDom(isInShadowDom);

					if (isInShadowDom) {
						const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
							`button, [href], input, select, textarea, 
  						[tabindex]:not([tabindex="-1"]):not([data-core-focus-trap])`,
						);
						firstFocusableRef.current = focusables[0];
						lastFocusableRef.current = focusables[focusables.length - 1];
					}
					// Focus modal on open
					dialogRef.current.focus();
				},
			}}
		>
			{inShadowDom && (
				<>
					<CoreFocusTrap onFocus={() => firstFocusableRef.current?.focus()} data-core-focus-trap />
					<CoreFocusTrap onFocus={() => lastFocusableRef.current?.focus()} data-core-focus-trap />
				</>
			)}
			{showTopCloseButton && <DialogCloseButton onClose={onTopClose ?? onClose} disabled={disableTopCloseButton} />}
			{onBack && !title && <DialogBackButton onBack={onBack} />}
			{title &&
				(onBack ? (
					<DialogHeaderWithBackButton onBack={onBack} titleId={titleId}>
						{title}
					</DialogHeaderWithBackButton>
				) : (
					<DialogHeader ariaId={titleId} verticalPadding={verticalPadding}>
						{title}
					</DialogHeader>
				))}

			<DialogContent
				tabIndex={-1}
				sx={{
					display: contentMatchModalHeight ? "flex" : undefined,
					placeContent: contentMatchModalHeight ? "center" : undefined,
					background: dialogContentBackground ?? undefined,
					...getScrollbarThemeOverrides(scrollbarWidth),
					"&&&": {
						padding: padding ?? (fullScreen && isMobileLayout ? "10px" : "24px"),
						margin: margin,
					},
				}}
			>
				{fullScreen && isMobileLayout ? (
					children
				) : (
					<div
						className={styles["message-dialog-content"]}
						style={
							contentMatchModalHeight
								? {
										margin: "0 auto",
										width: "100%",
										overflow: "visible",
									}
								: undefined
						}
					>
						{children}
					</div>
				)}
			</DialogContent>
			{!noActions && buttons !== undefined && (
				<DialogFooter
					footerVerticalPadding={footerVerticalPadding ?? (fullScreen && isMobileLayout ? "10px" : undefined)}
					footerHorizontalPadding={fullScreen && isMobileLayout ? "10px" : undefined}
				>
					{equalButtons ? (
						<Box
							sx={{
								display: "inline-grid",
								gridAutoColumns: "1fr",
								gap: "10px",
								"& button": {
									gridRow: 1,
								},
							}}
						>
							{showBottomCloseButton && (
								<CoreButton color="secondary" onClick={onClose}>
									{i18nPackage.t("coreComponents.modal.close")}
								</CoreButton>
							)}
							{Array.isArray(buttons)
								? buttons.map(
										({ hide, text, ...button }, index) =>
											!hide && (
												<CoreButton key={index} {...button}>
													{text}
												</CoreButton>
											),
									)
								: buttons}
						</Box>
					) : (
						<>
							{showBottomCloseButton && (
								<CoreButton color="secondary" onClick={onClose}>
									{i18nPackage.t("coreComponents.modal.close")}
								</CoreButton>
							)}
							{Array.isArray(buttons)
								? buttons.map(
										({ hide, text, ...button }, index) =>
											!hide && (
												<CoreButton key={index} {...button}>
													{text}
												</CoreButton>
											),
									)
								: buttons}
						</>
					)}
				</DialogFooter>
			)}
			{inShadowDom && <CoreFocusTrap data-core-focus-trap onFocus={() => firstFocusableRef.current?.focus()} />}
		</Dialog>
	);
};

export function CoreModalDescription({ sx, ...typographyProps }: TypographyProps) {
	return <Typography {...typographyProps} sx={{ fontSize: 15, fontWeight: 400, color: "#6B6B6B", ...sx }} />;
}
